import { describeChannelDestination, deliveryWarning } from "@/lib/notifications/recipients"
import type { Destination } from "./destination-list"
import type { ChannelOption } from "@/hooks/notifications/use-channel-options"
import type { NotificationChannelView } from "@/hooks/notifications/use-notification-channels"

export type DestinationCandidate = Destination & { isSystem: boolean }

/**
 * Apply the dispatcher's own rule to a saved selection: undeliverable channels stay listed
 * but flagged, and the system channel is added when it takes over. Callers map their own
 * channel shape to candidates; the rule lives here once.
 */
export function buildDestinations(
  candidates: DestinationCandidate[],
  selectedIds: string[],
): { destinations: Destination[]; systemChannelMissing: boolean } {
  const chosen = candidates.filter((c) => selectedIds.includes(c.id))
  const systemChannel = candidates.find((c) => c.isSystem) ?? null
  const systemTakesOver = chosen.length === 0 || chosen.every((c) => c.warning !== null)

  const destinations: Destination[] = [...chosen]
  if (systemTakesOver && systemChannel) {
    destinations.push({
      ...systemChannel,
      note: chosen.length === 0 ? "default" : "used instead",
    })
  }

  return { destinations, systemChannelMissing: systemTakesOver && !systemChannel }
}

/**
 * Recipients come from `config`, which only the full channel list carries; `enabled` and
 * `deliverable` come from the options projection, which is the server's verdict.
 */
export function toDestinationCandidates(
  channels: NotificationChannelView[],
  options: ChannelOption[],
): DestinationCandidate[] {
  return channels.map((channel) => {
    const option = options.find((o) => o.id === channel.id)
    const summary = describeChannelDestination(channel.type, channel.config)

    return {
      id: channel.id,
      type: channel.type,
      name: channel.name,
      isSystem: channel.isSystem,
      recipientsShort: summary.short,
      recipientsFull: summary.full,
      // Unknown to the options list (just deleted, or not yet refetched) — say nothing
      // rather than accuse a working channel.
      warning: option
        ? deliveryWarning({ type: channel.type, enabled: option.enabled, deliverable: option.deliverable })
        : null,
    }
  })
}
